<template>
	<div class="event-trend-histogram">
		<svg
			class="event-trend-svg"
			:width="svgWidth"
			:height="height"
			:viewBox="viewBox"
			preserveAspectRatio="none"
		>
			<line
				class="event-trend-baseline"
				:x1="0"
				:x2="svgWidth"
				:y1="height - 0.5"
				:y2="height - 0.5"
			/>
			<line
				v-if="maxBar"
				class="event-trend-max-line"
				:x1="0"
				:x2="svgWidth"
				:y1="maxBar.y"
				:y2="maxBar.y"
			/>
			<rect
				v-for="bar in bars"
				:key="bar.x"
				class="event-trend-bar"
				:x="bar.x"
				:y="bar.y"
				:width="bar.width"
				:height="bar.height"
			/>
			<g
				v-for="col in columns"
				:key="'hit-' + col.i"
				class="event-trend-hit-group"
			>
				<rect
					class="event-trend-hit-bg"
					:x="col.slotX"
					y="0"
					:width="col.slotWidth"
					:height="height"
				/>
				<rect
					class="event-trend-hit-bar"
					:x="col.x"
					:y="col.y"
					:width="col.width"
					:height="col.height"
				/>
				<rect
					class="event-trend-hitbox"
					:x="col.slotX"
					y="0"
					:width="col.slotWidth"
					:height="height"
				>
					<title>{{ col.tooltip }}</title>
				</rect>
			</g>
			<text
				v-if="maxBar"
				class="event-trend-max-label"
				:x="maxLabel.x"
				:y="Math.max(maxBar.y - 2, 2)"
				:text-anchor="maxLabel.anchor"
				dominant-baseline="text-after-edge"
			>
				{{ maxValue }}
			</text>
		</svg>
	</div>
</template>

<script>
import { computed, toRef } from 'vue';


export default {
	props: {
		record: {
			type: Object,
			default: () => ({}),
		},
		period: {
			type: String,
			default: '2d',
		},
		width: {
			type: Number,
			default: 120,
		},
		height: {
			type: Number,
			default: 45,
		},
	},
	setup(props) {
		const recordRef = toRef(props, 'record');
		const periodRef = toRef(props, 'period');
		const widthRef = toRef(props, 'width');
		const heightRef = toRef(props, 'height');
		const barWidthPx = 3;
		const barGapPx = 1;
		const eventsLabel = 'events';

		const trendItems = computed(() => {
			const record = recordRef.value || {};
			if (periodRef.value === '14d') {
				return record.trend_14d || [];
			}
			return record.trend_2d || [];
		});

		const maxValue = computed(() => {
			if (!trendItems.value.length) {
				return 0;
			}
			return Math.max(...trendItems.value.map(item => {
				if (!Array.isArray(item) || item.length < 2) {
					return 0;
				}
				return Number(item[1]) || 0;
			}));
		});

		const columns = computed(() => {
			if (!trendItems.value.length) {
				return [];
			}
			const max = maxValue.value;
			const maxForScale = max > 0 ? max : 1;
			const topPadding = 14;
			const bottomPadding = 1;
			const drawableHeight = Math.max(heightRef.value - topPadding - bottomPadding, 1);
			const result = [];
			for (let i = 0; i < trendItems.value.length; i++) {
				const item = trendItems.value[i];
				const bucket = Array.isArray(item) && item.length > 0 ? String(item[0]) : '';
				const value = Array.isArray(item) && item.length > 1 ? (Number(item[1]) || 0) : 0;
				const barHeight = value > 0 ? Math.max((value / maxForScale) * drawableHeight, 1) : 0;
				const slotX = i * (barWidthPx + barGapPx);
				result.push({
					i,
					slotX,
					slotWidth: barWidthPx + barGapPx,
					x: slotX,
					y: heightRef.value - bottomPadding - barHeight,
					width: barWidthPx,
					height: barHeight,
					value,
					tooltip: bucket + '\n' + eventsLabel + ': ' + value,
				});
			}
			return result;
		});

		const bars = computed(() => columns.value.filter(col => col.value > 0));

		const maxBar = computed(() => {
			if (maxValue.value <= 0) {
				return null;
			}
			if (!bars.value.length) {
				return null;
			}
			const max = maxValue.value;
			return bars.value.find(bar => bar.value === max) || null;
		});

		const maxLabel = computed(() => {
			const bar = maxBar.value;
			if (!bar) {
				return { x: 0, anchor: 'middle' };
			}
			const edgeBars = 3;
			const lastIndex = trendItems.value.length - 1;
			if (bar.i < edgeBars) {
				return { x: bar.x, anchor: 'start' };
			}
			if (bar.i > lastIndex - edgeBars) {
				return { x: bar.x + bar.width, anchor: 'end' };
			}
			return { x: bar.x + (bar.width / 2), anchor: 'middle' };
		});

		const svgWidth = computed(() => {
			if (!trendItems.value.length) {
				return widthRef.value;
			}
			return (trendItems.value.length * barWidthPx) + ((trendItems.value.length - 1) * barGapPx);
		});
		const viewBox = computed(() => `0 0 ${svgWidth.value} ${heightRef.value}`);

		return {
			maxValue,
			columns,
			bars,
			maxBar,
			maxLabel,
			svgWidth,
			viewBox,
		};
	},
}
</script>

<style scoped>
.event-trend-histogram {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	background: transparent;
	padding: 2px 4px;
	border-radius: 2px;
	line-height: 1;
}
.event-trend-svg {
	display: block;
}
.event-trend-baseline {
	stroke: #595959;
	stroke-width: 0.5;
	opacity: 0.6;
}
.event-trend-bar {
	fill: #595959;
	shape-rendering: crispEdges;
}
.event-trend-hit-bg {
	fill: transparent;
	pointer-events: none;
	shape-rendering: crispEdges;
}
.event-trend-hit-bar {
	fill: transparent;
	pointer-events: none;
	shape-rendering: crispEdges;
}
.event-trend-hitbox {
	fill: transparent;
	pointer-events: all;
	cursor: pointer;
}
.event-trend-hit-group:hover .event-trend-hit-bg {
	fill: #f0f0f0;
}
.event-trend-hit-group:hover .event-trend-hit-bar {
	fill: #1890ff;
}
.event-trend-max-line {
	stroke: #d9d9d9;
	stroke-width: 0.5;
}
.event-trend-max-label {
	fill: #595959;
	font-size: 11px;
	font-variant-numeric: tabular-nums;
	pointer-events: none;
}
</style>
